import React, { memo, useEffect, useRef, useState } from 'react'
import { Form, Overlay, Popover } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { IErrMsg } from './types'
import { DescriptionBtnWithPopup } from '../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from './components/RiskyParam/RiskyParamUnlockBtn'

export interface IIntegerRangeInput {
    className?: string
    state: any
    touched?: any
    errors?: any
    errorText?: string
    name: string
    label?: string
    placeholder?: string
    isDisabled?: boolean
    tooltipText?: string
    changeWarning?: string
    isColumn?: boolean
    isLabelHidden?: boolean
    type?: string
    isRequired?: boolean
    isFeedbackFixed?: boolean
    errMsg?: IErrMsg
    onEnter?: Function
    onBlur?: Function

    setState(state: any): any
    setTouched?(touched: any): any
}

export const IntegerRangeInput = memo(function IntegerRangeInput({
    setState,
    setTouched,
    className,
    state,
    touched,
    errors,
    errMsg,
    name,
    label,
    placeholder,
    tooltipText,
    changeWarning,
    isDisabled,
    isColumn,
    isLabelHidden,
    type,
    isRequired,
    isFeedbackFixed,
    onEnter,
    onBlur,
}: IIntegerRangeInput) {
    const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)
    const [showTooltip, setShowTooltip] = useState(false)
    const inputRef = useRef(null)

    const isInvalid = touched && touched[name] && errors && errors[name]

    const handleUnlockRiskyParam = () => setIsRiskyParam(false)

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
            setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
            setState((prev: any) => ({ ...prev, [name]: value }))
    }

    const handleFocus = () => {
        setShowTooltip(true)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setShowTooltip(false)
        if (typeof onBlur === 'function') return onBlur(e)
        setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const handleOnKeyDown = (evt: any) => {
        if (evt.key === 'Enter' && typeof onEnter === 'function') {
            onEnter()
        }
    }

    useEffect(() => {
        if (state?.[name] === '')
            setState((prev: any) => ({ ...prev, [name]: null }))
    }, [state?.[name]]) // eslint-disable-line

    return (
        <Form.Group
            style={{ maxWidth: '600px' }}
            className={`${className} ${
                isInvalid && !isFeedbackFixed && 'mb-4'
            } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
        >
            {label && !isLabelHidden ? (
                <Form.Label
                    style={{
                        maxWidth: isColumn ? '30%' : 'auto',
                        opacity: isRiskyParam ? '50%' : '100%',
                    }}
                    className="label-main mt-1 text-capital pe-2"
                >
                    <span className="label-main-wrapper p-relative">
                        {label}:{' '}
                        {isRequired && <RequiredMark />}
                    </span>
                </Form.Label>
            ) : null}
            <div
                style={{ width: '70%' }}
                className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
            >
                    <span className="d-flex align-items-center flex-column w-100 p-relative">
                        {isRiskyParam && <div className="input-overlay" />}
                    <Form.Control
                        ref={inputRef}
                        className={`input-main ${
                            tooltipText && 'input-padding'
                        }`}
                        type="string"
                        placeholder={placeholder}
                        value={state?.[name] ?? ''}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleOnKeyDown}
                        disabled={!!isDisabled}
                        isInvalid={isInvalid}
                        name={name}
                        tabIndex={isRiskyParam ? -1 : 0}
                    />
                    <Form.Control.Feedback
                        className="input-feedback"
                        type="invalid"
                    >
                        <FormattedMessage
                            id={errMsg?.message || 'field-validation-error'}
                            values={errMsg?.values}
                            tagName="span"
                        />
                    </Form.Control.Feedback>
                    <Overlay target={inputRef.current} show={showTooltip} placement="top">
                        {(props) => (
                            <Popover id="range-tooltip" className="px-2 py-1" {...props}>
                               <FormattedMessage id="tooltips.enterRange" />
                            </Popover>
                        )}
                    </Overlay>
                {tooltipText && (
                    <DescriptionBtnWithPopup
                        type={'default'}
                        header={label ?? ''}
                        description={tooltipText ?? ''}
                        className="mt-1 input-tooltip"
                    />
                )}
                </span>
                {isRiskyParam && (
                    <RiskyParamUnlockBtn
                        onUnlockConfirm={handleUnlockRiskyParam}
                        changeWarning={changeWarning}
                    />
                )}
            </div>
        </Form.Group>
    )
})
